<template>
  <div
    :class="['message', `message--${message.role}`, { 'message--wide': hasWideWidget, 'message--full': pdpFullWidthWidget }]"
    :style="bubbleStyle"
  >
    <div
      v-if="message.role === 'assistant'"
      class="message__author"
    >
      <svg>
        <use
          width="14"
          height="14"
          href="/img/icons/bot.svg#icon-bot"
        />
      </svg>
    </div>
    <div
      class="message__content"
      :class="{ 'message__content--has-save': canSaveToClipboard }"
      :style="hasWideWidget ? { flex: 1, minWidth: 0 } : {}"
    >
      <div
        v-if="canSaveToClipboard"
        class="message__save-clipboard-wrap"
      >
        <button
          type="button"
          class="message__save-clipboard"
          :class="`message__save-clipboard--${message.role}`"
          aria-label="Save to Clipboard"
          @click.stop="onSaveToClipboard"
        >
          <span
            class="message__save-clipboard-tooltip"
            role="tooltip"
          >
            {{ clipboardSaved ? 'Saved' : 'Save to Clipboard' }}
          </span>
          <svg
            v-if="clipboardSaved"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect
              width="8"
              height="4"
              x="8"
              y="2"
              rx="1"
              ry="1"
            />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          </svg>
        </button>
      </div>
      <p v-html="message.content" />
      <div
        v-if="shouldShowProgress"
        class="message__progress"
      >
        <div
          class="message__progress-bar"
          :style="progressBarStyle"
        />
      </div>
      <div
        v-if="visibleAttachments.length"
        :class="['message__attachments-grid', 'message__attachments-grid--' + visibleAttachments.length]"
      >
        <div
          v-for="(att, idx) in visibleAttachments"
          :key="'attachment-' + idx"
          class="message__attachment small"
          @click="onOpenAttachment(att)"
        >
          <template v-if="att.type === 'url'">
            <img
              :src="att.payload"
              :alt="getFileName(att)"
              class="message__attachment-img"
            >
            <div
              v-if="att._mrhiCallouts && att._mrhiCallouts.type === 'cambridge'"
              class="message__attachment-cambridge-callouts"
            >
              <div class="message__attachment-cambridge-flavor">
                {{ att._mrhiCallouts.flavorText }}
              </div>
              <div class="message__attachment-cambridge-count">
                {{ att._mrhiCallouts.countText }}
              </div>
            </div>
            <img
              v-if="att.overlayUrl"
              :src="att.overlayUrl"
              class="message__attachment-product-overlay"
              alt="Product preview"
            >
            <div class="message__attachment-img-overlay">
              <span class="message__attachment-img-name">{{ getFileName(att) }}</span>
            </div>
            <div
              v-if="idx === 0 && shouldShowModeControl"
              class="message__attachment-mode"
              @click.stop
            >
              <button
                type="button"
                class="message__attachment-mode-icon"
                aria-label="Product image mode"
                @click.stop="toggleModePopover"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M7.765.13a.5.5 0 0 1 .47 0l7.5 4a.5.5 0 0 1 0 .882l-7.5 4a.5.5 0 0 1-.47 0l-7.5-4a.5.5 0 0 1 0-.882z" />
                  <path d="m2.125 8.567-1.86.992a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882l-1.86-.992-5.17 2.756a1.5 1.5 0 0 1-1.41 0z" />
                </svg>
              </button>
              <div
                v-if="modePopoverOpen"
                class="message__attachment-mode-popover"
                @click.stop
              >
                <div class="message__attachment-mode-label">
                  Product image
                </div>
                <div class="message__attachment-mode-buttons">
                  <button
                    type="button"
                    :class="['message__attachment-mode-btn', { 'is-active': currentProductMode === 'separate' }]"
                    @click.stop="onSelectProductMode('separate')"
                  >
                    Layered
                  </button>
                  <button
                    type="button"
                    :class="['message__attachment-mode-btn', { 'is-active': currentProductMode === 'integrated' }]"
                    @click.stop="onSelectProductMode('integrated')"
                  >
                    Static
                  </button>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="message__attachment-file">
              <div :class="['message__attachment-file-icon', `message__attachment-file-icon--${getFileExt(att)}`]">
                <i
                  :class="getFileIconClass(att)"
                  aria-hidden="true"
                />
              </div>
              <span class="message__attachment-file-name">{{ getFileName(att) }}</span>
              <span class="message__attachment-file-ext">{{ getFileExt(att) }}</span>
            </div>
          </template>

          <div
            v-if="idx === maxVisible - 1 && remainingAttachments > 0"
            class="message__attachment-remaining"
          >
            +{{ remainingAttachments }}
          </div>

          <div
            v-if="att.type === 'url'"
            class="message__attachment-download-icon"
            title="Download"
            @click.stop="onDownloadAttachment(att)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
          </div>
        </div>
      </div>
      <p
        v-if="message.postContent"
        class="mt-2"
        v-html="message.postContent"
      />
      <img
        v-if="message.imageUrl"
        class="mt-2"
        :src="message.imageUrl"
        style="width: 200px; height: auto"
        alt="img"
      >
      <div
        v-if="message.bannerResizeSettings"
        class="message__actions"
      >
        <b-button
          variant="secondary"
          :disabled="message.disabled"
          @click="onDuplicateTemplateAndPrepareResizeData"
        >
          Confirm
        </b-button>
      </div>
      <div
        v-if="message.proofLink"
        class="message__actions"
      >
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="message.disabled"
          @click="onCopyProofLink"
        >
          <i
            class="fa fa-clipboard"
            aria-hidden="true"
          />
          Copy
        </button>
      </div>
      <button
        v-if="message.hasSelectButton"
        class="btn btn-primary mt-2"
        :disabled="message.disabled"
        @click="onSelectProduct"
      >
        Select this product
      </button>

      <div
        v-if="(message.productOptions && message.productOptions.length > 0) || message.hasOpenSelectorButton"
        class="mt-2 product-options-container"
      >
        <div
          v-for="option in message.productOptions"
          :key="option.id"
          class="product-option"
          :class="{ 'product-option--disabled': message.disabled }"
          @click="message.disabled ? null : onSelectProductOption(option)"
        >
          <input
            :id="'option-' + option.id"
            type="radio"
            :name="'product-option-' + index"
            class="mr-2"
            style="cursor: pointer;"
            :disabled="message.disabled"
          >
          <img
            :src="option.thumbnailUrl || option.full_url || option.path"
            class="product-option-img"
            alt="Product thumbnail"
          >
        </div>
        <button
          class="btn btn-outline-primary btn-sm mt-3"
          :disabled="message.disabled"
          @click="onOpenProductSelector"
        >
          View the product image selector...
        </button>
      </div>

      <div
        v-if="message.widgets && message.widgets.length"
        class="mt-2 chat-widgets-container"
      >
        <template v-for="widget in message.widgets">
          <AIChatWidgetRadio
            v-if="widget.type === 'radio'"
            :key="'widget-' + widget.id"
            :options="widget.options"
            :disabled="message.disabled"
            :value="getWidgetValue(widget.id) || (widget.options.length ? widget.options[0].value : null)"
            @select="onWidgetRadioSelect(widget.id, $event)"
          />
          <AIChatWidgetDropdown
            v-if="widget.type === 'dropdown'"
            :key="'widget-' + widget.id"
            :options="widget.options"
            :placeholder="widget.placeholder"
            :label="widget.label"
            :disabled="message.disabled"
            :visible="isWidgetVisible(widget)"
            :value="getWidgetValue(widget.id)"
            @select="onWidgetDropdownSelect(widget.id, $event)"
          />
          <AIChatWidgetActionButtons
            v-if="widget.type === 'action_buttons'"
            :key="'widget-' + widget.id"
            :buttons="widget.buttons"
            :disabled="message.disabled"
            :selected-action="widget.selectedAction || ''"
            :stacked="widget.stacked || false"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetTemplateSlider
            v-if="widget.type === 'template_slider'"
            :key="'widget-' + widget.id"
            :templates="widget.templates"
            :disabled="message.disabled"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetTemplateGallery
            v-if="widget.type === 'template_gallery'"
            :key="'widget-' + widget.id"
            :templates="widget.templates"
            :preselected-ids="widget.preselectedIds || []"
            :disabled="message.disabled"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetSequencePicker
            v-if="widget.type === 'sequence_picker'"
            :key="'widget-' + widget.id"
            :sources="widget.sources || []"
            :initial-source-id="widget.initialSourceId || ''"
            :initial-selected-keys="widget.initialSelectedKeys || []"
            :disabled="message.disabled"
            @confirm="onWidgetSequenceConfirm(widget.id, $event)"
          />
          <AIChatWidgetMobileHeroStylePicker
            v-if="widget.type === 'mobile_hero_style_picker'"
            :key="'widget-' + widget.id"
            :header="widget.header"
            :cards="widget.cards || []"
            :skip-action="widget.skipAction || ''"
            :skip-label="widget.skipLabel || 'Skip this image'"
            :disabled="message.disabled"
            :selected-action="widget.selectedAction || ''"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetReferenceImagesPicker
            v-if="widget.type === 'reference_images_picker'"
            :key="'widget-' + widget.id"
            :images="widget.images || []"
            :base-product="widget.base_product || ''"
            :disabled="message.disabled"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetPdpImageSetPicker
            v-if="widget.type === 'pdp_image_set_picker' && !legacyPdpUi"
            :key="'widget-' + widget.id"
            :rows="widget.rows || []"
            :templates-by-subtype="(widget.templatesBySubtype && !Array.isArray(widget.templatesBySubtype)) ? widget.templatesBySubtype : {}"
            :preset-sources="widget.presetSources || []"
            :initial-preset-id="widget.initialPresetId || ''"
            :initial-size="widget.initialSize || ''"
            :product-label="widget.productLabel || ''"
            :product-image-url="widget.productImageUrl || ''"
            :own-image-result="widget.ownImageResult || null"
            :brand-logo-result="widget.brandLogoResult || null"
            :product-image-result="widget.productImageResult || null"
            :disabled="message.disabled"
            :sequential-cursor="widget.sequentialCursor != null ? widget.sequentialCursor : null"
            :reference-mode="widget.referenceMode || false"
            :brief-id="widget.briefId || null"
            :brief-title="widget.briefTitle || ''"
            @confirm="onWidgetAction(widget.id, `pdp_row_picker_confirm:${JSON.stringify($event)}`)"
            @open-brief="onWidgetAction(widget.id, 'brief_open_sidebar')"
            @open-brief-full="onWidgetAction(widget.id, 'brief_open_full')"
            @open-gallery="onWidgetAction(widget.id, 'pdp_open_design_gallery')"
            @own-image-request="onWidgetAction(widget.id, `pdp_own_image_request:${JSON.stringify($event)}`)"
            @brand-logo-request="onWidgetAction(widget.id, `pdp_brand_logo_request:${JSON.stringify($event)}`)"
            @product-image-request="onWidgetAction(widget.id, `pdp_product_image_request:${JSON.stringify($event)}`)"
            @reference-submit="onWidgetAction(widget.id, `pdp_reference_submit:${JSON.stringify($event)}`)"
            @row-update="onWidgetAction(widget.id, `pdp_row_update:${JSON.stringify($event)}`)"
          />
          <AIChatWidgetPdpImageSetPickerLegacy
            v-if="widget.type === 'pdp_image_set_picker' && legacyPdpUi"
            :key="'widget-legacy-' + widget.id"
            :rows="widget.rows || []"
            :templates-by-subtype="(widget.templatesBySubtype && !Array.isArray(widget.templatesBySubtype)) ? widget.templatesBySubtype : {}"
            :preset-sources="widget.presetSources || []"
            :initial-preset-id="widget.initialPresetId || ''"
            :product-image-url="widget.productImageUrl || ''"
            :own-image-result="widget.ownImageResult || null"
            :brand-logo-result="widget.brandLogoResult || null"
            :disabled="message.disabled"
            :reference-mode="widget.referenceMode || false"
            @confirm="onWidgetAction(widget.id, `pdp_row_picker_confirm:${JSON.stringify($event)}`)"
            @open-gallery="onWidgetAction(widget.id, 'pdp_open_design_gallery')"
            @own-image-request="onWidgetAction(widget.id, `pdp_own_image_request:${JSON.stringify($event)}`)"
            @brand-logo-request="onWidgetAction(widget.id, `pdp_brand_logo_request:${JSON.stringify($event)}`)"
          />
          <AIChatWidgetPdpScenePicker
            v-if="widget.type === 'pdp_scene_picker'"
            :key="'widget-' + widget.id"
            :row-index="widget.rowIndex"
            :default-scene-prompt="widget.defaultScenePrompt || ''"
            :disabled="message.disabled"
            @composite="onWidgetAction(widget.id, `pdp_scene_composite:${JSON.stringify($event)}`)"
            @skip="onWidgetAction(widget.id, 'pdp_scene_skip')"
          />
          <AIChatWidgetPdpParallelGrid
            v-if="widget.type === 'pdp_parallel_grid' && message._pdpParallelGrid && !legacyPdpUi"
            :key="'widget-' + widget.id"
            :grid="message._pdpParallelGrid"
            :message-index="index"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetPdpParallelGridLegacy
            v-if="widget.type === 'pdp_parallel_grid' && message._pdpParallelGrid && legacyPdpUi"
            :key="'widget-legacy-' + widget.id"
            :grid="message._pdpParallelGrid"
            :message-index="index"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatWidgetAbHeroPicker
            v-if="widget.type === 'ab_hero_picker' && message._abHeroForm"
            :key="'widget-' + widget.id"
            :rows="message._abHeroForm.rows || []"
            :generating="message._abHeroForm.generating || false"
            :size="message._abHeroForm.size || '2000x2000'"
            :image-result="message._abHeroForm.imageResult || null"
            :disabled="message.disabled"
            :message-index="index"
            @action="onWidgetAction(widget.id, $event)"
            @paste-image="$emit('widget-action', { widgetId: widget.id, action: { action: 'ab_hero_paste', payload: $event }, messageIndex: index, message })"
          />
          <AIChatWidgetAbHero
            v-if="widget.type === 'ab_hero' && message._abHeroForm"
            :key="'widget-' + widget.id"
            :rows="message._abHeroForm.rows || []"
            :generating="message._abHeroForm.generating || false"
            :size="message._abHeroForm.size || '2000x2000'"
            :image-result="message._abHeroForm.imageResult || null"
            :result="message._abHeroResult"
            :disabled="message.disabled"
            :message-index="index"
            @action="onWidgetAction(widget.id, $event)"
            @paste-image="$emit('widget-action', { widgetId: widget.id, action: { action: 'ab_hero_paste', payload: $event }, messageIndex: index, message })"
          />
          <AIChatWidgetAbHeroResult
            v-if="widget.type === 'ab_hero_result' && message._abHeroResult"
            :key="'widget-' + widget.id"
            :grid="message._abHeroResult"
            :message-index="index"
            @action="onWidgetAction(widget.id, $event)"
          />
          <AIChatResolvedProductCards
            v-if="widget.type === 'resolved_product_cards'"
            :key="'widget-' + widget.id"
            :references="widget.references || []"
            @action="onWidgetAction(widget.id, $event)"
          />
        </template>
      </div>
    </div>
    <div
      v-if="lightboxAttachment"
      class="message__lightbox"
      @click.self="closeLightbox"
      @keydown.esc="closeLightbox"
    >
      <button
        type="button"
        class="message__lightbox-close"
        aria-label="Close preview"
        @click="closeLightbox"
      >
        &times;
      </button>
      <div class="message__lightbox-stage">
        <img
          :src="lightboxAttachment.payload"
          class="message__lightbox-img"
          alt=""
        >
        <div
          v-if="lightboxAttachment._mrhiCallouts && lightboxAttachment._mrhiCallouts.type === 'cambridge'"
          class="message__lightbox-cambridge-callouts"
        >
          <div class="message__lightbox-cambridge-flavor">
            {{ lightboxAttachment._mrhiCallouts.flavorText }}
          </div>
          <div class="message__lightbox-cambridge-count">
            {{ lightboxAttachment._mrhiCallouts.countText }}
          </div>
        </div>
        <img
          v-if="lightboxAttachment.overlayUrl"
          :src="lightboxAttachment.overlayUrl"
          class="message__lightbox-overlay"
          alt="Product preview"
        >
      </div>
    </div>
  </div>
</template>

<script>
import AIChatWidgetRadio from './widgets/AIChatWidgetRadio.vue';
import AIChatWidgetDropdown from './widgets/AIChatWidgetDropdown.vue';
import AIChatWidgetActionButtons from './widgets/AIChatWidgetActionButtons.vue';
import AIChatWidgetTemplateSlider from './widgets/AIChatWidgetTemplateSlider.vue';
import AIChatWidgetTemplateGallery from './widgets/AIChatWidgetTemplateGallery.vue';
import AIChatWidgetSequencePicker from './widgets/AIChatWidgetSequencePicker.vue';
import AIChatWidgetMobileHeroStylePicker from './widgets/AIChatWidgetMobileHeroStylePicker.vue';
import AIChatWidgetReferenceImagesPicker from './widgets/AIChatWidgetReferenceImagesPicker.vue';
import AIChatWidgetPdpImageSetPicker from './widgets/AIChatWidgetPdpImageSetPicker.vue';
import AIChatWidgetPdpImageSetPickerLegacy from './widgets/AIChatWidgetPdpImageSetPickerLegacy.vue';
import AIChatWidgetPdpScenePicker from './widgets/AIChatWidgetPdpScenePicker.vue';
import AIChatWidgetPdpParallelGrid from './widgets/AIChatWidgetPdpParallelGrid.vue';
import AIChatWidgetPdpParallelGridLegacy from './widgets/AIChatWidgetPdpParallelGridLegacy.vue';
import AIChatResolvedProductCards from './widgets/AIChatResolvedProductCards.vue';
import AIChatWidgetAbHeroPicker from './widgets/AIChatWidgetAbHeroPicker.vue';
import AIChatWidgetAbHero from './widgets/AIChatWidgetAbHero.vue';
import AIChatWidgetAbHeroResult from './widgets/AIChatWidgetAbHeroResult.vue';
import { downloadFileFromUrl, downloadAttachmentComposite } from '@frontend/utils';
import { htmlToPlainText } from '@frontend/components/common/clipboard/clipboard-formatters';
import { isLegacyPdpUi } from '@frontend/store/creative-studio/pdp-ui-flags';

export default {
  components: {
    AIChatWidgetRadio,
    AIChatWidgetDropdown,
    AIChatWidgetActionButtons,
    AIChatWidgetTemplateSlider,
    AIChatWidgetTemplateGallery,
    AIChatWidgetSequencePicker,
    AIChatWidgetMobileHeroStylePicker,
    AIChatWidgetReferenceImagesPicker,
    AIChatWidgetPdpImageSetPicker,
    AIChatWidgetPdpImageSetPickerLegacy,
    AIChatWidgetPdpScenePicker,
    AIChatWidgetPdpParallelGrid,
    AIChatWidgetPdpParallelGridLegacy,
    AIChatResolvedProductCards,
    AIChatWidgetAbHeroPicker,
    AIChatWidgetAbHero,
    AIChatWidgetAbHeroResult,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    index: {
      type: [Number, String],
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'duplicate-template-and-prepare-resized-data',
    'copy-proof-link',
    'select-product',
    'select-product-option',
    'open-product-selector',
    'widget-action',
    'save-to-clipboard',
  ],
  data() {
    return {
      maxVisible: 4,
      lightboxAttachment: null,
      modePopoverOpen: false,
      clipboardSaved: false,
      progressNow: Date.now(),
      batchProgressTimer: null,
    };
  },
  computed: {
    // POR-13766 — revert switch for the reworked PDP image-set widgets.
    legacyPdpUi() {
      return isLegacyPdpUi();
    },
    hasWideWidget() {
      return (this.message.widgets || []).some((w) => w.type === 'template_slider' || w.type === 'template_gallery' || w.type === 'pdp_image_set_picker' || w.type === 'pdp_parallel_grid' || w.type === 'ab_hero');
    },
    // POR-13766 — an ACTIVE (not-yet-confirmed) PDP picker fills the full (widened)
    // content column; once confirmed (disabled) it drops back to the normal
    // wide-widget bubble, and the generation view uses the normal width too. Legacy
    // PDP UI keeps the 95% behavior.
    pdpFullWidthWidget() {
      if (this.legacyPdpUi || this.message.disabled) {
        return false;
      }

      return (this.message.widgets || []).some((w) => w.type === 'pdp_image_set_picker');
    },
    bubbleStyle() {
      if (this.pdpFullWidthWidget) {
        return { maxWidth: '100%', width: '100%' };
      }

      if (this.hasWideWidget) {
        return { maxWidth: '95%', width: '95%' };
      }

      return {};
    },
    visibleAttachments() {
      const attachments = this.message.attachments || [];
      return attachments.slice(0, this.maxVisible);
    },
    remainingAttachments() {
      const attachments = this.message.attachments || [];
      const count = attachments.length - this.maxVisible;
      return count > 0 ? count : 0;
    },
    shouldShowModeControl() {
      const ctx = this.message?._pdpImageContext;
      return !!(ctx && ctx.hasProductRefs);
    },
    currentProductMode() {
      return this.message?._pdpImageContext?.productMode || 'integrated';
    },
    /**
     * Show the progress bar only while the operation it tracks is plausibly
     * still in flight. Recoverable batch jobs remain visible until their
     * persisted status becomes terminal. Other progress messages use three guards:
     *   1. The message must declare a duration.
     *   2. It must be the LAST message in the chat. The work that the bar
     *      tracks always emits a follow-up message ("Lifestyle ready", an
     *      attachment, a failure card, etc.) — so any message followed by
     *      another one is, by definition, finished.
     *   3. It must have a start timestamp newer than the declared duration.
     *      This catches the edge case where the chat is reopened mid-flight
     *      and the LAST message is still the progress one but the duration
     *      has already elapsed.
     *
     * Messages without a start timestamp (legacy entries persisted before
     * the timestamp was introduced) hide the bar immediately.
     */
    shouldShowProgress() {
      const dur = this.message?._progressDurationMs;
      if (!dur) return false;
      if (['pending', 'processing'].includes(this.message?._batchLayeredPdpJob?.status)) return true;
      if (!this.isLast) return false;
      const startedAt = this.message?._progressStartedAt;
      if (!startedAt) return false;
      return Date.now() - startedAt < dur;
    },
    activeBatchProgress() {
      return ['pending', 'processing'].includes(this.message?._batchLayeredPdpJob?.status);
    },
    batchProgressPercent() {
      const startedAt = Number(this.message?._progressStartedAt);
      if (!startedAt) return 0;
      const elapsedSeconds = Math.max(0, (this.progressNow - startedAt) / 1000);
      return Math.min(95, elapsedSeconds * 0.5);
    },
    plainTextContent() {
      const parts = [
        htmlToPlainText(this.message?.content),
        htmlToPlainText(this.message?.postContent),
      ].filter(Boolean);
      return parts.join('\n\n').trim();
    },

    canSaveToClipboard() {
      return Boolean(this.plainTextContent) || this.abHeroClipboardPrompts.length > 0;
    },

    // A/B hero result messages carry the two variant prompts on their tiles. The
    // generic message text ("Start with the source product hero…") isn't worth
    // reusing, so "Save to Clipboard" saves each variant's ACTUAL prompt as its
    // own clipboard entry instead. Tiles with no prompt (e.g. a "Use original"
    // variant) are skipped. (POR-13583)
    abHeroClipboardPrompts() {
      const tiles = this.message && this.message._abHeroResult && this.message._abHeroResult.tiles;
      if (!Array.isArray(tiles)) return [];
      return tiles
        .map((tile) => ({
          title: (tile && tile.label) || '',
          content: String((tile && tile.description) || '').trim(),
        }))
        .filter((prompt) => prompt.content);
    },

    progressBarStyle() {
      if (this.activeBatchProgress) {
        return {
          animation: 'none',
          transform: `translateX(-${100 - this.batchProgressPercent}%)`,
          transition: 'transform 1s linear',
        };
      }
      const dur = this.message?._progressDurationMs || 30000;
      const startedAt = this.message?._progressStartedAt;
      if (startedAt) {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, dur - elapsed);
        return {
          animationDuration: dur + 'ms',
          animationDelay: -elapsed + 'ms',
          animationPlayState: remaining > 0 ? 'running' : 'paused',
        };
      }
      return { animationDuration: dur + 'ms' };
    },
  },
  watch: {
    'message._batchLayeredPdpJob.status': function () {
      this.syncBatchProgressTimer();
    },
    'message.attachments': {
      handler: async function (attachments = []) {
        for (const att of attachments) {
          if (att.type === 'file' && att.payload instanceof File) {
            try {
              att.payload = await this.readFileAsDataURL(att.payload);
              att.type = 'url';
            } catch (err) {
              console.error('Failed to read file as data URL', err);
            }
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Initialize _widgetValues with default radio selections so they're available
    // even if the user clicks Apply without changing the pre-selected option
    if (this.message.widgets && this.message.widgets.length) {
      if (!this.message._widgetValues) {
        this.$set(this.message, '_widgetValues', {});
      }
      this.message.widgets.forEach(widget => {
        if (widget.type === 'radio' && widget.options && widget.options.length && !this.message._widgetValues[widget.id]) {
          this.$set(this.message._widgetValues, widget.id, widget.options[0].value);
        }
      });
    }
    this.syncBatchProgressTimer();
  },
  beforeDestroy() {
    this.stopBatchProgressTimer();
    document.removeEventListener('keydown', this.onLightboxKeydown);
    document.removeEventListener('mousedown', this.onDocumentClickForPopover);
  },
  methods: {
    syncBatchProgressTimer() {
      this.progressNow = Date.now();
      if (!this.activeBatchProgress) {
        this.stopBatchProgressTimer();
        return;
      }
      if (!this.batchProgressTimer) {
        this.batchProgressTimer = setInterval(() => {
          this.progressNow = Date.now();
        }, 1000);
      }
    },
    stopBatchProgressTimer() {
      if (this.batchProgressTimer) {
        clearInterval(this.batchProgressTimer);
        this.batchProgressTimer = null;
      }
    },
    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(e);
        reader.readAsDataURL(file);
      });
    },
    getFileName(att) {
      if (att.name) return att.name;
      if (att.type === 'url' && typeof att.payload === 'string') {
        const name = att.payload.split('/').pop()?.split('?')[0];
        return name || 'File';
      }
      if (att.type === 'file' && att.payload?.name) {
        return att.payload.name;
      }
      return 'File';
    },
    getFileExt(att) {
      const name = this.getFileName(att);
      const ext = name.split('.').pop()?.toUpperCase();
      return ext || '';
    },
    getFileIconClass(att) {
      const ext = this.getFileExt(att);
      const map = {
        PDF: 'fa fa-file-pdf-o',
        DOC: 'fa fa-file-word-o',
        DOCX: 'fa fa-file-word-o',
        XLS: 'fa fa-file-excel-o',
        XLSX: 'fa fa-file-excel-o',
        ZIP: 'fa fa-file-archive-o',
        RAR: 'fa fa-file-archive-o',
        PPT: 'fa fa-file-powerpoint-o',
        PPTX: 'fa fa-file-powerpoint-o',
        TXT: 'fa fa-file-text-o',
        CSV: 'fa fa-file-text-o',
      };
      return map[ext] || 'fa fa-file-o';
    },
    onOpenAttachment(att) {
      if (att.type === 'url') {
        // Build a self-contained snapshot so the lightbox always has the
        // overlay URL even if Vue 2 reactivity didn't pick up the property
        // when it was dynamically added to the attachment object. Falls back
        // to the message's PDP context for the same reason.
        const overlayUrl = att.overlayUrl
          || this.message?._pdpImageContext?.overlayUrl
          || null;
        this.lightboxAttachment = {
          ...att,
          overlayUrl,
        };
        document.addEventListener('keydown', this.onLightboxKeydown);
      } else if (att.type === 'file') {
        const fileUrl = URL.createObjectURL(att.payload);
        window.open(fileUrl, '_blank');
      }
    },
    closeLightbox() {
      this.lightboxAttachment = null;
      document.removeEventListener('keydown', this.onLightboxKeydown);
    },
    onLightboxKeydown(e) {
      if (e.key === 'Escape') this.closeLightbox();
    },
    onCopyProofLink() {
      this.$emit('copy-proof-link', `proof-link-${this.index}`, this.message);
    },
    onSelectProduct() {
      this.$emit('select-product', this.message.productId, this.message);
    },
    onDuplicateTemplateAndPrepareResizeData() {
      this.$emit('duplicate-template-and-prepare-resized-data', this.message.newWidth, this.message.newHeight, this.message.bannerResizeSettings, this.message);
    },
    onSelectProductOption(option) {
      this.$emit('select-product-option', option);
    },
    onOpenProductSelector() {
      this.$emit('open-product-selector', this.message);
    },
    getWidgetValue(widgetId) {
      if (!this.message._widgetValues) return '';
      return this.message._widgetValues[widgetId] || '';
    },
    isWidgetVisible(widget) {
      if (!widget.dependsOn) return true;
      const depValue = this.getWidgetValue(widget.dependsOn.widgetId);
      return depValue === widget.dependsOn.value;
    },
    onWidgetRadioSelect(widgetId, value) {
      if (!this.message._widgetValues) {
        this.$set(this.message, '_widgetValues', {});
      }
      this.$set(this.message._widgetValues, widgetId, value);
    },
    onWidgetDropdownSelect(widgetId, value) {
      if (!this.message._widgetValues) {
        this.$set(this.message, '_widgetValues', {});
      }
      this.$set(this.message._widgetValues, widgetId, value);
    },
    onWidgetAction(widgetId, action) {
      this.$emit('widget-action', {
        widgetId,
        action,
        messageIndex: this.index,
        message: this.message,
      });
    },
    onWidgetSequenceConfirm(widgetId, payload) {
      this.$emit('widget-sequence-confirm', {
        widgetId,
        payload,
        messageIndex: this.index,
        message: this.message,
      });
    },
    toggleModePopover(e) {
      this.modePopoverOpen = !this.modePopoverOpen;
      if (this.modePopoverOpen) {
        document.addEventListener('mousedown', this.onDocumentClickForPopover);
      } else {
        document.removeEventListener('mousedown', this.onDocumentClickForPopover);
      }
      if (e?.currentTarget?.blur) e.currentTarget.blur();
    },
    onDocumentClickForPopover(e) {
      if (!this.$el.contains(e.target)) {
        this.modePopoverOpen = false;
        document.removeEventListener('mousedown', this.onDocumentClickForPopover);
      }
    },
    onSelectProductMode(mode) {
      this.modePopoverOpen = false;
      document.removeEventListener('mousedown', this.onDocumentClickForPopover);
      if (this.currentProductMode === mode) return;
      this.$emit('widget-action', {
        widgetId: 'pdp_image_mode',
        action: `pdp_image_mode:${mode}`,
        messageIndex: this.index,
        message: this.message,
      });
    },
    onSaveToClipboard() {
      // A/B hero results save the two variant prompts as separate clipboard
      // entries; every other message saves its plain-text content. (POR-13583)
      const abPrompts = this.abHeroClipboardPrompts;
      const payload = abPrompts.length
        ? { prompts: abPrompts }
        : { content: this.plainTextContent };

      if (!abPrompts.length && !payload.content) {
        return;
      }

      this.$emit('save-to-clipboard', {
        ...payload,
        message: this.message,
        index: this.index,
      });

      this.clipboardSaved = true;
      setTimeout(() => { this.clipboardSaved = false; }, 3000);
    },

    async onDownloadAttachment(att) {
      const fileName = this.getFileName(att);
      const hasOverlay = !!att.overlayUrl;
      const hasCallouts = !!(att._mrhiCallouts && att._mrhiCallouts.type === 'cambridge');
      if (hasOverlay || hasCallouts) {
        await downloadAttachmentComposite(att, fileName);
      } else {
        await downloadFileFromUrl(att.payload, fileName);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 80%;
  width: fit-content;
  margin-bottom: 16px;
  /* Smoothly animate the bubble resize when the PDP picker toggles full-width. (POR-13766) */
  transition: max-width 0.35s ease, width 0.35s ease;

  &--user {
    margin-left: auto;
    max-width: calc(80% - 20px);

    .message__content {
      // Darker green for stronger contrast against white text (POR-13596)
      background-color: #379C75;
      color: #fff;
      border-radius: 10px;
    }
  }

  &--assistant {
    margin-right: auto;

    .message__content {
      background-color: #F2F5F9;
      color: #212529;
      border-radius: 10px;
    }
  }

  &__author {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #EEF8F5;
    border-radius: 100%;
    margin-top: 2px;

    svg {
      width: 14px;
      height: 14px;
      color: #209c86;
    }
  }

  &__content {
    position: relative;
    padding: 10px 14px;
    line-height: 1.5;

    &--has-save {
      // Reserve horizontal space for the floating clipboard button
      // without shifting the message content down.
      padding-right: 46px;
    }

    p {
      white-space: pre-wrap;
      word-break: break-word;
      margin-bottom: 0;
      font-size: 14px;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
}

.message__save-clipboard-wrap {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.message__save-clipboard {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 150ms ease, background 150ms ease;
}

.message__content:hover .message__save-clipboard,
.message__save-clipboard:focus-visible {
  opacity: 1;
}

.message__save-clipboard--user {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.message__save-clipboard--user:hover,
.message__save-clipboard--user:focus-visible {
  background: rgba(255, 255, 255, 0.32);
}

.message__save-clipboard--assistant {
  background: rgba(17, 24, 39, 0.06);
  color: #4b5563;
}

.message__save-clipboard--assistant:hover,
.message__save-clipboard--assistant:focus-visible {
  background: rgba(17, 24, 39, 0.1);
  color: #111827;
}

.message__save-clipboard-tooltip {
  position: absolute;
  right: 36px;
  bottom:0;
  display: inline-block;
  padding: 5px 14px;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 12px !important;
  font-weight: 500;
  line-height: 1.1;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  opacity: 0;
  pointer-events: none;
  transform: translateY(4px);
  transition: opacity 150ms ease, transform 150ms ease;
  overflow: hidden;
}

.message__save-clipboard:hover .message__save-clipboard-tooltip,
.message__save-clipboard:focus-visible .message__save-clipboard-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.message__attachments-grid {
  display: grid;
  gap: 8px;
  margin-top: 8px;
  grid-template-columns: repeat(2, 1fr);
}

/* Single attachment → render at full width (one column) and larger size. */
.message__attachments-grid--1 {
  grid-template-columns: 1fr;
  width: 420px;
  max-width: 100%;

  .message__attachment.small,
  .message__attachment.big {
    height: 420px;
  }
}

.message__attachment {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  background-color: #ffffff;
  border: 1px solid #e8ecf1;
  transition: all 0.2s ease;
}

.message__attachment.big {
  height: 240px;
}

.message__attachment.small {
  height: 220px;
}

.message__attachment-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.message__attachment-product-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80%;
  height: 90%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.3s ease;
}

.message__attachment:hover .message__attachment-product-overlay {
  transform: translate(-50%, -50%) scale(1.08);
}

.message__lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  cursor: zoom-out;
  animation: message__lightbox-fade 0.15s ease-out;
}

@keyframes message__lightbox-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.message__lightbox-close {
  position: absolute;
  top: 16px;
  right: 24px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 40px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 12px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.message__lightbox-close:hover {
  opacity: 1;
}

.message__lightbox-stage {
  position: relative;
  display: inline-block;
  line-height: 0;
  cursor: default;
}

.message__lightbox-img {
  display: block;
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.message__lightbox-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80%;
  height: 90%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
}

.message__attachment:hover {
  border-color: rgba(55, 156, 117, 0.4);
  box-shadow: 0 4px 16px -4px rgba(55, 156, 117, 0.15);
  transform: scale(1.02);
}

.message__attachment:hover .message__attachment-img {
  transform: scale(1.08);
}

.message__attachment:hover .message__attachment-img-overlay,
.message__attachment:hover .message__attachment-download-icon {
  opacity: 1;
  pointer-events: auto;
}

.message__attachment-img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 6px 8px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message__attachment-img-name {
  font-size: 11px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message__attachment-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fafbfc 0%, #f0f3f7 100%);
}

.message__attachment-remaining {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message__attachment-mode {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 20;
}

.message__attachment-mode-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  color: #c0c5cd;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  padding: 0;
}

.message__attachment-mode-icon:hover {
  background: #e5e7eb;
  color: #9aa0a8;
}

.message__attachment-mode-icon:focus,
.message__attachment-mode-icon:active {
  outline: none;
  color: #c0c5cd;
  background: #f3f4f6;
}

.message__attachment-mode-popover {
  position: absolute;
  left: 0;
  bottom: calc(100% + 6px);
  min-width: 230px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 10px 28px -4px rgba(0, 0, 0, 0.18), 0 6px 12px -6px rgba(0, 0, 0, 0.1);
  padding: 12px 14px;
  cursor: default;
  z-index: 30;
}

.message__attachment-mode-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.message__attachment-mode-buttons {
  display: flex;
  gap: 6px;
}

.message__attachment-mode-btn {
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;
}

.message__attachment-mode-btn:hover {
  background: #f3f4f6;
}

.message__attachment-mode-btn.is-active {
  background: #e6f4ef;
  color: #1f7a5a;
  font-weight: 600;
}

.message__attachment-mode-btn:focus {
  outline: none;
}

.message__attachment-download-icon {
  position: absolute;
  left: 46px;
  bottom: 8px;
  width: 30px;
  height: 30px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  color: #c0c5cd;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
  z-index: 20;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #e5e7eb;
    color: #9aa0a8;
  }
}

.product-options-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
  background-color: #fff;
  border: 1px solid #e2e8f0;

  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
}

.product-option-img {
  width: 60px;
  height: 60px;
  object-fit: contain;
  background-color: white;
  border: 1px solid #f1f5f9;
  border-radius: 4px;
}

.product-option--disabled {
  opacity: 0.6;
  pointer-events: none;
  filter: grayscale(0.5);
}

.chat-widgets-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message__progress {
  margin-top: 8px;
  height: 4px;
  width: 100%;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.message__progress-bar {
  height: 100%;
  width: 100%;
  background: linear-gradient(90deg, #379C75 0%, #2E8D68 100%);
  transform: translateX(-100%);
  animation-name: message__progress-fill;
  animation-duration: 30s;
  animation-timing-function: cubic-bezier(0.2, 0.6, 0.4, 1);
  animation-fill-mode: forwards;
}

@keyframes message__progress-fill {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

// Cambridge MRHI callouts rendered as deterministic CSS overlays on top of
// the plain-white Gemini bg. The two rectangles AND the placeholder text
// match the geometry the backend uses to compose the final layout, so the
// chat preview and the saved layout look identical (Gemini's pixel-level
// rectangle drawing was inconsistent across products — this side-steps it).
// Geometry: top rectangle x 78-100%, y 0-73%; bottom x 78-100%, y 78-100%.
.message__attachment-cambridge-callouts {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.message__attachment-cambridge-callouts::before,
.message__attachment-cambridge-callouts::after {
  content: '';
  position: absolute;
  right: 0;
  width: 22%;
  background: #000;
  // ::after is the last child of its parent, so without an explicit z-index
  // the bottom rectangle would render on top of the Count text and hide it.
  z-index: 0;
}

.message__attachment-cambridge-callouts::before {
  top: 0;
  height: 73%;
}

.message__attachment-cambridge-callouts::after {
  top: 78%;
  height: 22%;
}

.message__attachment-cambridge-flavor,
.message__attachment-cambridge-count {
  z-index: 1;
}

.message__attachment-cambridge-flavor {
  position: absolute;
  left: 89%;
  top: 36.5%;
  transform: translate(-50%, -50%) rotate(-90deg);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

.message__attachment-cambridge-count {
  position: absolute;
  left: 89%;
  top: 89%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
}

.message__lightbox-cambridge-callouts {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}

.message__lightbox-cambridge-callouts::before,
.message__lightbox-cambridge-callouts::after {
  content: '';
  position: absolute;
  right: 0;
  width: 22%;
  background: #000;
  z-index: 0;
}

.message__lightbox-cambridge-callouts::before {
  top: 0;
  height: 73%;
}

.message__lightbox-cambridge-callouts::after {
  top: 78%;
  height: 22%;
}

.message__lightbox-cambridge-flavor,
.message__lightbox-cambridge-count {
  z-index: 1;
}

.message__lightbox-cambridge-flavor {
  position: absolute;
  left: 89%;
  top: 36.5%;
  transform: translate(-50%, -50%) rotate(-90deg);
  color: #fff;
  font-weight: 700;
  font-size: 32px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

.message__lightbox-cambridge-count {
  position: absolute;
  left: 89%;
  top: 89%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
}
</style>
